import { APICategory, SyncFrequencyPlan } from "../../../../models/Entities";
import { firstLetterUpperCase } from "../../../../services";
import {
  BillingPlan,
  CurrentCategoryCharge,
  OrganizationBillingPlan,
  PaymentMethod,
  isCreditCard,
  BillingPlanTier,
  OrganizationBillingPlanCounts,
} from "./BillingModels";

export const parseCategoryUnitCountsFromBillingPlan = (
  orgBillingPlan: OrganizationBillingPlan,
  orgBillingPlanCounts: OrganizationBillingPlanCounts,
): CurrentCategoryCharge[] => {
  return Object.entries(orgBillingPlanCounts.linked_accounts_count).map(([category, quantity]) => {
    const priceCents = orgBillingPlan.billing_plan.linked_account_category_prices_cents[category];
    return {
      category: category as APICategory,
      unit: "Linked Account",
      quantity,
      priceCents,
    };
  });
};

/**
 * WARNING!!! These billing plan fields should not be used to gate features.
 *
 * Instead we should generally use Product Restriction (see useProductRestrictions hook).
 * This allows individually granting access to features
 */
export const isBillingPlanFreePlan = (billingPlan?: BillingPlan): boolean =>
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_FREE;
export const isBillingPlanLaunchPlan = (billingPlan?: BillingPlan): boolean =>
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_LAUNCH;
// feature set wise free and launch are the same (except free is only 3 LA and launch is > 3 LA)
export const isBillingPlanFreeLaunch = (billingPlan?: BillingPlan): boolean =>
  isBillingPlanFreePlan(billingPlan) || isBillingPlanLaunchPlan(billingPlan);
// feature set wise enterprise and expand are the same
export const isBillingPlanEnterpriseExpand = (billingPlan?: BillingPlan): boolean =>
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_ENTERPRISE ||
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_EXPAND;
// feature set wise professional and grow are the same
export const isBillingPlanProfessionalGrow = (billingPlan?: BillingPlan): boolean =>
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_PROFESSIONAL ||
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_PROFESSIONAL_STARTUP ||
  billingPlan?.plan_tier === BillingPlanTier.BILLING_PLAN_TIER_GROW;

export function getBillingPlanTitle(
  organizationBillingPlan:
    | OrganizationBillingPlan
    | Omit<OrganizationBillingPlan, "organization">
    | undefined,
) {
  switch (organizationBillingPlan?.billing_plan?.plan_tier) {
    case BillingPlanTier.BILLING_PLAN_TIER_CORE_STARTUP:
      return firstLetterUpperCase(BillingPlanTier.BILLING_PLAN_TIER_CORE);
    case BillingPlanTier.BILLING_PLAN_TIER_PROFESSIONAL_STARTUP:
      return firstLetterUpperCase(BillingPlanTier.BILLING_PLAN_TIER_PROFESSIONAL);
    default:
      return firstLetterUpperCase(organizationBillingPlan?.billing_plan?.plan_tier || "");
  }
}

export function formatSyncFrequncyPlans(syncFrequencyPlan: string) {
  switch (syncFrequencyPlan) {
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_HIGHEST:
      return "Highest";
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_HIGHEST_WITH_DELETED_DATA:
      return "Highest with Deleted Data";
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_HIGHEST_MANUAL:
      return "Highest manual";
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_DAILY:
      return "Daily";
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_MONTHLY:
      return "Monthly";
    case SyncFrequencyPlan.SYNC_FREQUENCY_PLAN_QUARTERLY:
      return "Quarterly";
  }
  return "";
}

export function imageURLForCardBrand(paymentMethod: PaymentMethod) {
  if (isCreditCard(paymentMethod)) {
    switch (paymentMethod.brand.toLowerCase()) {
      case "amex":
        return "https://merge-fe-static-assets.s3.amazonaws.com/amex.svg";
      case "diners":
        return "https://merge-fe-static-assets.s3.amazonaws.com/diners_club.png";
      case "visa":
        return "https://merge-fe-static-assets.s3.amazonaws.com/visa.svg";
      case "mastercard":
        return "https://merge-fe-static-assets.s3.amazonaws.com/mastercard.png";
      case "discover":
        return "https://merge-fe-static-assets.s3.amazonaws.com/discover.png";
      case "jcb":
        return "https://merge-fe-static-assets.s3.amazonaws.com/jcb.png";
      default:
        return "#";
    }
  } else {
    return "https://merge-fe-static-assets.s3.amazonaws.com/bank.png";
  }
}

export const toTitleCase = (phrase: string) =>
  phrase
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
